import { useQuery } from "@tanstack/react-query";
import { createBlockerMask, getBlockerMaskMeta, getBlockerMaskPng } from "../api/rest";
import { BlockerField } from "./blockerField";

/**
 * ADR 370 — the warehouse's blocker mask, decoded for the walk camera.
 *
 * A warehouse normally has no mask row (ADR 343 left creation to its
 * consumers), so the first walk on a warehouse creates one — the frame is
 * derived from the warehouse's own geometry and the rack layer is recomputed
 * on read, so the row is a cache key, not authored data. A viewer without
 * write access, or a warehouse with no geometry, simply walks without
 * collision (spec invariant 4: absent mask = today's behaviour).
 */
async function loadField(siteId: string, warehouseId: string): Promise<BlockerField | null> {
  let meta = await getBlockerMaskMeta(siteId, warehouseId);
  if (!meta) {
    try {
      meta = await createBlockerMask(siteId, warehouseId);
    } catch {
      return null;
    }
  }
  const blob = await getBlockerMaskPng(siteId, warehouseId);
  if (!blob) return null;
  const bitmap = await createImageBitmap(blob);
  // Read the size BEFORE close(): a closed ImageBitmap reports 0 x 0.
  const { width, height } = bitmap;
  if (width !== meta.widthPx || height !== meta.heightPx) {
    bitmap.close?.();
    throw new Error(`blocker mask: image ${width}x${height} does not match its frame ${meta.widthPx}x${meta.heightPx}`);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) { bitmap.close?.(); return null; }
  ctx.drawImage(bitmap, 0, 0);
  const px = ctx.getImageData(0, 0, width, height).data;
  bitmap.close?.();
  return BlockerField.fromPixels(meta, px);
}

export function useBlockerField(siteId: string | null | undefined, warehouseId: string | null | undefined, enabled: boolean): BlockerField | null {
  const { data } = useQuery({
    queryKey: ["blocker-field", siteId, warehouseId],
    queryFn: () => loadField(siteId!, warehouseId!),
    enabled: enabled && !!siteId && !!warehouseId,
    staleTime: 5 * 60_000,
    retry: false,
  });
  return data ?? null;
}
